import { Shield, FileText, Lock, CheckCircle } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import GDPRChecklist from '@/components/GDPRChecklist';

export default function Privacy() {
  return (
    <div className="animate-slide-up max-w-4xl">
      {/* Page Header */}
      <div className="page-header">
        <h1 className="page-title">Privacy & Compliance</h1>
        <p className="page-subtitle">Review our data protection policies and compliance certifications.</p>
      </div>

      <Tabs defaultValue="overview" className="w-full">
        <TabsList className="w-full justify-start border-b border-border rounded-none bg-transparent h-auto p-0 mb-6">
          <TabsTrigger value="overview" className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent px-4 py-3">Overview</TabsTrigger>
          <TabsTrigger value="gdpr" className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent px-4 py-3">GDPR</TabsTrigger>
        </TabsList>

        <TabsContent value="overview">
          {/* Compliance Status */}
          <div className="stat-card mb-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-lg bg-success/10 flex items-center justify-center">
                <CheckCircle className="w-6 h-6 text-success" />
              </div>
              <div>
                <h2 className="font-semibold text-foreground">Compliance Status</h2>
                <p className="text-sm text-success">All systems compliant</p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {[
                { name: 'SOC 2 Type II', status: 'Certified', date: 'Valid until Dec 2025' },
                { name: 'GDPR', status: 'Compliant', date: 'Last audit: Nov 2024' },
                { name: 'ISO 27001', status: 'Certified', date: 'Valid until Oct 2025' },
              ].map((cert, index) => (
                <div key={index} className="p-4 bg-secondary rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <Shield className="w-5 h-5 text-primary" />
                    <span className="font-medium text-foreground">{cert.name}</span>
                  </div>
                  <p className="text-sm text-success mb-1">{cert.status}</p>
                  <p className="text-xs text-muted-foreground">{cert.date}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Data Protection */}
          <div className="stat-card mb-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
                <Lock className="w-6 h-6 text-primary" />
              </div>
              <div>
                <h2 className="font-semibold text-foreground">Data Protection</h2>
                <p className="text-sm text-muted-foreground">How we protect your data</p>
              </div>
            </div>

            <div className="space-y-4">
              {[
                {
                  title: 'Encryption at Rest',
                  description: 'All data is encrypted using AES-256 encryption when stored.',
                },
                {
                  title: 'Encryption in Transit',
                  description: 'All communications are secured using TLS 1.3 encryption.',
                },
                {
                  title: 'Access Controls',
                  description: 'Role-based access controls ensure only authorized users can access sensitive data.',
                },
                {
                  title: 'Regular Backups',
                  description: 'Automated backups are performed daily with 30-day retention.',
                },
              ].map((item, index) => (
                <div key={index} className="p-4 bg-secondary rounded-lg">
                  <h3 className="font-medium text-foreground mb-1">{item.title}</h3>
                  <p className="text-sm text-muted-foreground">{item.description}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Documents */}
          <div className="stat-card">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
                <FileText className="w-6 h-6 text-primary" />
              </div>
              <div>
                <h2 className="font-semibold text-foreground">Legal Documents</h2>
                <p className="text-sm text-muted-foreground">Review our policies and agreements</p>
              </div>
            </div>

            <div className="space-y-3">
              {[
                { name: 'Privacy Policy', lastUpdated: 'December 2024' },
                { name: 'Terms of Service', lastUpdated: 'December 2024' },
                { name: 'Data Processing Agreement', lastUpdated: 'November 2024' },
                { name: 'Security Whitepaper', lastUpdated: 'October 2024' },
              ].map((doc, index) => (
                <button
                  key={index}
                  className="w-full flex items-center justify-between p-4 bg-secondary rounded-lg hover:bg-secondary/80 transition-colors text-left"
                >
                  <div className="flex items-center gap-3">
                    <FileText className="w-5 h-5 text-muted-foreground" />
                    <span className="font-medium text-foreground">{doc.name}</span>
                  </div>
                  <span className="text-sm text-muted-foreground">Updated: {doc.lastUpdated}</span>
                </button>
              ))}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="gdpr">
          <GDPRChecklist />
        </TabsContent>
      </Tabs>
    </div>
  );
}
